import * as z from "zod";
import { decimal, pagination, version } from "../index";
import { aggregateSyncDto, MAX_LINE_UNITS, syncStatuses } from "../til";
import { movementMessages, movementStatuses, movementTypes } from "./enums";
import { movementExecutionDto } from "./movement-execution";

// Stock movements of the F1 (M12, FR-M12-03…08, ADR-141; 50-api/logistics.md): Logistics asks to move units between
// two warehouses; within a company it is a transfer, towards another company of the group an intercompany transfer
// order, which Operations confirms (gate `logistics.intercompanyTransfer`). Confirmed, the movement goes to the
// warehouse (I-10) and to Business Central (I-25) in the same step.

/** Who reads the movements; who asks for one, cancels it and sends it again; who confirms an intercompany one. */
export const MOVEMENT_READ = "movement.read";
export const MOVEMENT_REQUEST = "movement.request";
export const MOVEMENT_CONFIRM = "movement.confirm";

/** Lines a movement may hold: what a message carries in parts (I-10 §3). */
export const MAX_MOVEMENT_LINES = 3000;

const reason = z.string().trim().min(1).max(2000);

/**
 * FR-M12-03, FR-M12-06: the warehouses, the SKUs with their units and why. The kind follows from the warehouses (a
 * warehouse is of one company, ADR-123 point 8); number and customer code are Nexxt's (FR-M12-07). The id is the
 * client's (ADR-046): the same request again answers the movement it created.
 */
export const requestStockMovementInput = z.object({
  id: z.uuid(),
  fromLocationId: z.uuid(),
  toLocationId: z.uuid(),
  memo: z.string().trim().min(1).max(4000),
  lines: z
    .array(z.object({ skuId: z.uuid(), qty: z.number().int().positive().max(MAX_LINE_UNITS) }))
    .min(1)
    .max(MAX_MOVEMENT_LINES),
});
export type RequestStockMovementInput = z.input<typeof requestStockMovementInput>;

export const getStockMovementInput = z.object({ movementId: z.uuid() });
export type GetStockMovementInput = z.input<typeof getStockMovementInput>;
/** A movement by its id: the path of the routes of one movement. */
export const stockMovementParams = getStockMovementInput.pick({ movementId: true });

/** An action on a movement: `version` is the one the client read (409 otherwise). */
export const stockMovementActionInput = getStockMovementInput.extend({ version });
export type StockMovementActionInput = z.input<typeof stockMovementActionInput>;
export const stockMovementActionBody = stockMovementActionInput.omit({ movementId: true });

/** Operations refuses an intercompany transfer, or Logistics cancels a movement: always with the reason. */
export const stockMovementReasonInput = stockMovementActionInput.extend({ reason });
export type StockMovementReasonInput = z.input<typeof stockMovementReasonInput>;
export const stockMovementReasonBody = stockMovementReasonInput.omit({ movementId: true });

/** One message of a confirmed movement, sent again as a new revision. */
export const resyncStockMovementInput = stockMovementActionInput.extend({ message: z.enum(movementMessages) });
export type ResyncStockMovementInput = z.input<typeof resyncStockMovementInput>;
export const resyncStockMovementBody = resyncStockMovementInput.omit({ movementId: true });

const listFilters = {
  status: z.enum(movementStatuses).optional(),
  type: z.enum(movementTypes).optional(),
  /** A warehouse the units leave or arrive at. */
  location: z.uuid().optional(),
};

/** The movements, the latest asked first. */
export const listStockMovementsInput = z.object({
  ...listFilters,
  /** Part of the number of the movement, case-insensitive and without separators (ADR-117). */
  q: z.string().trim().min(1).max(80).optional(),
  ...pagination({ defaultSize: 50, maxSize: 200 }),
});
export type ListStockMovementsInput = z.input<typeof listStockMovementsInput>;

/** How many movements with the same filters, and since when the oldest was asked (ADR-086): a queue of the home. */
export const countStockMovementsInput = z.object(listFilters);
export type CountStockMovementsInput = z.input<typeof countStockMovementsInput>;

const warehouse = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  /** `reference.legal_entity.code`: a warehouse is of one company (ADR-123 point 8). */
  company: z.string(),
});

/** A movement as its list shows it. */
export const stockMovementSummaryDto = z
  .object({
    id: z.uuid(),
    /** Generated by Nexxt, never typed (FR-M12-07): how every system names the movement. */
    movementNumber: z.string(),
    type: z.enum(movementTypes),
    status: z.enum(movementStatuses),
    from: warehouse,
    to: warehouse,
    /** The customer code of the destination warehouse, for an intercompany transfer (FR-M12-07). */
    customerCode: z.string().nullable(),
    memo: z.string().nullable(),
    /** The number of the document Business Central registered, from the outcome of I-25. */
    bcDocumentNo: z.string().nullable(),
    requestedAt: z.iso.datetime().nullable(),
    confirmedAt: z.iso.datetime().nullable(),
    /** When its cancellation left for the two systems, which have still to answer. */
    cancelRequestedAt: z.iso.datetime().nullable(),
    cancelReason: z.string().nullable(),
    /** How the warehouse (I-10) and Business Central (I-25) took its last state. */
    wmsSyncStatus: z.enum(syncStatuses),
    bcSyncStatus: z.enum(syncStatuses),
    /** When the warehouse closed the step `shipped`, and the step `received` (I-11, ADR-142): both, it is executed. */
    shippedAt: z.iso.datetime().nullable(),
    receivedAt: z.iso.datetime().nullable(),
    totalUnits: z.number().int(),
    lineCount: z.number().int(),
    version: z.number().int(),
  })
  .meta({ id: "StockMovementSummary" });
export type StockMovementSummaryDto = z.output<typeof stockMovementSummaryDto>;

/** A movement with its lines: alone and after an action. */
export const stockMovementDto = stockMovementSummaryDto
  .extend({
    requestedBy: z.uuid().nullable(),
    confirmedBy: z.uuid().nullable(),
    lines: z.array(
      z.object({
        id: z.uuid(),
        sku: z.object({ id: z.uuid(), skuCode: z.string().nullable(), ean: z.string().nullable() }),
        qty: z.number().int(),
        /** The units free in the warehouse the units leave when the movement was asked (FR-M12-04). */
        atpAtRequest: z.number().int().nullable(),
        /**
         * An intercompany transfer (ADR-249): the value of one unit, from the brand's rule when it was asked, and its
         * currency; null within a company.
         */
        unitPrice: decimal.nullable(),
        currency: z.string().nullable(),
        /** The units the warehouse says left, and arrived (I-11): the sum of its executions. */
        qtyShipped: z.number().int(),
        qtyReceived: z.number().int(),
      }),
    ),
    /** What the warehouse reported, the first first (I-11 `kind: movement`). */
    executions: z.array(movementExecutionDto),
  })
  .meta({ id: "StockMovement" });
export type StockMovementDto = z.output<typeof stockMovementDto>;

/** How a movement stands towards the warehouse and Business Central: one state per message (ADR-141). */
export const stockMovementSyncDto = z
  .object({
    movementId: z.uuid(),
    messages: z.array(aggregateSyncDto.extend({ message: z.enum(movementMessages) })),
  })
  .meta({ id: "StockMovementSync" });
export type StockMovementSyncDto = z.output<typeof stockMovementSyncDto>;
